/**
 * Where the queue survives a closed tab.
 *
 * IndexedDB rather than localStorage, for one reason that decides it: IndexedDB
 * stores a Blob directly. localStorage is strings only, so every photograph
 * would have to be base64-encoded — a third larger, re-encoded on every read,
 * and against a 5 MB quota that eight shots would blow on their own.
 *
 * Every method resolves rather than rejecting. A device with IndexedDB blocked
 * (private mode on some browsers, an old WebView) should degrade to a queue
 * that works until the tab closes, not to a capture screen that throws.
 */

import type { QueueItem, QueueStore } from './queue.js';

const DB_NAME = 'estimator-uploads';
const STORE = 'queue';
const VERSION = 1;

function open(): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    let req: IDBOpenDBRequest;
    try {
      req = indexedDB.open(DB_NAME, VERSION);
    } catch {
      resolve(null);
      return;
    }
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE, { keyPath: 'id' });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => resolve(null);
    // Another tab holding an old version open. Rare, and not worth blocking a
    // capture over — the in-memory queue still works for this session.
    req.onblocked = () => resolve(null);
  });
}

function run<T>(
  db: IDBDatabase,
  mode: IDBTransactionMode,
  fn: (store: IDBObjectStore) => IDBRequest,
): Promise<T | null> {
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(STORE, mode);
      const req = fn(tx.objectStore(STORE));
      req.onsuccess = () => resolve(req.result as T);
      req.onerror = () => resolve(null);
      tx.onabort = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

/**
 * A store that persists, falling back to memory when IndexedDB is unavailable.
 *
 * The fallback is deliberate rather than an error: losing the queue when the
 * tab closes is bad, but refusing to let someone photograph a car is worse.
 */
export function createQueueStore(): QueueStore {
  const memory = new Map<string, QueueItem>();
  let dbPromise: Promise<IDBDatabase | null> | null = null;
  const db = () => (dbPromise ??= open());

  return {
    async all() {
      const handle = await db();
      if (!handle) return [...memory.values()];
      const rows = await run<QueueItem[]>(handle, 'readonly', (s) => s.getAll());
      return rows ?? [...memory.values()];
    },

    async put(item) {
      memory.set(item.id, item);
      const handle = await db();
      if (handle) await run(handle, 'readwrite', (s) => s.put(item));
    },

    async remove(id) {
      memory.delete(id);
      const handle = await db();
      if (handle) await run(handle, 'readwrite', (s) => s.delete(id));
    },
  };
}

/** True when IndexedDB actually opened, so the UI can be honest about it. */
export async function queueIsPersistent(): Promise<boolean> {
  return (await open()) !== null;
}
